import React, {useState, useEffect, useRef} from 'react';
import classnames from 'classnames';
import withSizes from 'react-sizes';
import URL from 'url-parse';

import {trackEvent, events} from '../../tracking';
import {GeoCountry, getCategoryKeyByTabAndGeo, TopStoriesCategory} from '../../api';
import {ApiResult, getFreshDomainThumbnailUrls, clearShownList} from '../../api/domainThumbnails';

import styles from './styles.css';

interface IndexPagePreviewsProps {
  children?: React.ReactNode;
  query?: string | null;
  noMoreResults?: boolean;
  resultsLoading?: boolean;
  maxItems?: number;
  hideHeading?: boolean;
  gridLayout?: boolean;
  loose?: boolean;
  topStoriesPage?: number;
  topStoriesTab?: TopStoriesCategory;
  layoutBreakpoint?: 'mobile' | 'threecolumn' | 'fourcolumn';
  sourceGeo?: GeoCountry;
  refreshStamp?: number | null;
}

const THUMBS_PER_PAGE = 4;
const THUMBS_PER_PAGE_SPLASH = 12;
const LOOSE_FIRST_STEP = 10;
const LOOSE_FIRST_STEP_THREE_COLUMN = 8;

export const jumpToPreviews = () => {
  const element = document.body.querySelector('[data-live-homepages]');

  if (element) {
    element.scrollIntoView();
  }
};

const IndexPagePreviewsBase = ({
  children,
  query,
  noMoreResults,
  resultsLoading,
  hideHeading,
  maxItems,
  gridLayout,
  loose,
  topStoriesPage,
  topStoriesTab,
  layoutBreakpoint,
  sourceGeo = 'gb',
  refreshStamp = null
}: IndexPagePreviewsProps) => {
  let thumbsPerPage = maxItems || (query ? THUMBS_PER_PAGE : THUMBS_PER_PAGE_SPLASH);
  const [thumbnailUrls, setThumbnailUrls] = useState<ApiResult[]>([]);
  const moreButtonRef = useRef<HTMLDivElement>(null);
  const [isInViewport, setIsInViewport] = useState(false);
  const [waitingForMoreResults, setWaitingForMoreResults] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const topStoriesKey = topStoriesTab && getCategoryKeyByTabAndGeo(topStoriesTab, sourceGeo);

  if (loose && !thumbnailUrls.length && !maxItems && !query) {
    thumbsPerPage = layoutBreakpoint === 'threecolumn' ? LOOSE_FIRST_STEP_THREE_COLUMN : LOOSE_FIRST_STEP;
  }

  useEffect(() => {
    if (maxItems) {
      return;
    }

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setIsInViewport(true);
        } else {
          setIsInViewport(false);
        }
      }
    };
    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '700px'
    });

    if (moreButtonRef.current) {
      observer.observe(moreButtonRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    let aborted = false;

    clearShownList();
    setWaitingForMoreResults(true);
    (async () => {
      try {
        const urls = await getFreshDomainThumbnailUrls(thumbsPerPage, {
          repeatForever: !loose,
          sourceGeo
        });

        if (!aborted) {
          setThumbnailUrls(urls);
          setWaitingForMoreResults(false);
        }
      } catch (error) {
        setError(error);
      }
    })().catch();

    return () => {
      aborted = true;
    };
  }, [sourceGeo]);

  useEffect(() => {
    if (maxItems) {
      return;
    }

    let aborted = false;

    if (isInViewport && !waitingForMoreResults && !noMoreResults && !resultsLoading) {
      setWaitingForMoreResults(true);
      (async () => {
        try {
          const urls = await getFreshDomainThumbnailUrls(thumbsPerPage, {
            repeatForever: !loose && !gridLayout,
            sourceGeo
          });

          if (!aborted) {
            setThumbnailUrls([...thumbnailUrls, ...urls]);
            setWaitingForMoreResults(false);
          }
        } catch (error) {
          setError(error);
        }
      })().catch();
    }

    return () => {
      aborted = true;
    };
  }, [isInViewport, noMoreResults, resultsLoading]);

  const pages = thumbnailUrls.map(({url, thumb}, index) => {
    const host = new URL(url).host;

    return (
      <a
        className={styles.indexPagePreview}
        href={url}
        key={`${url}${index}`}
        onClick={() =>
          trackEvent(events.PAGE_VIEW_EVENT, {
            channel: host,
            id: host,
            layoutBreakpoint,
            position: index + 1,
            query: query || undefined,
            subchannel: query ? 'indexpage_search' : 'indexpage_home',
            title: host,
            topStoriesKey
          })
        }
        rel="noopener"
        style={{
          backgroundImage: `url("${thumb}${refreshStamp ? `?t=${refreshStamp}` : ''}")`
        }}
        target="_blank"
      >
        <div className={styles.messageBox}>
          <div>Visit</div>
          <div>{host}</div>
        </div>
        <div className={styles.domainName}>{host}</div>
      </a>
    );
  });

  const offsetRightPages =
    layoutBreakpoint === 'mobile' ? 0 : ((topStoriesPage || 0) + 1) * (layoutBreakpoint === 'fourcolumn' ? 4 : 2);

  const heading = (
    <div className={styles.heading} data-live-homepages>
      <span className={styles.live}>LIVE</span> Homepages
    </div>
  );

  return (
    <div
      className={classnames(
        query && !gridLayout && styles.resultsContainer,
        maxItems && styles.splashRightPanel,
        !query && !maxItems && styles.splashContainer,
        gridLayout && styles.resultsGridContainer,
        loose && styles.looseGrid
      )}
      data-top-stories-page={topStoriesPage}
    >
      {!hideHeading && !loose && heading}
      {!loose && <div className={styles.indexPagePreviews}>{pages}</div>}
      {loose && (
        <div className={styles.topStoriesAndRightWrapper}>
          {children}
          <div className={styles.splashRightPanel}>
            {heading}
            {pages.slice(0, offsetRightPages)}
          </div>
        </div>
      )}
      {loose && <div className={styles.loosePages}>{pages.slice(offsetRightPages)}</div>}
      {!maxItems && <div className={styles.moreButton} ref={moreButtonRef} />}
      {error && <div className={styles.error}>{error.message}</div>}
    </div>
  );
};

const mapSizesToProps = ({width}: {width: number}): {layoutBreakpoint: IndexPagePreviewsProps['layoutBreakpoint']} => {
  if (width < 768) {
    return {layoutBreakpoint: 'mobile'};
  }

  if (width < 964) {
    return {layoutBreakpoint: 'threecolumn'};
  }

  return {layoutBreakpoint: 'fourcolumn'};
};

export const IndexPagePreviews = withSizes<IndexPagePreviewsProps, IndexPagePreviewsProps>(mapSizesToProps)(
  IndexPagePreviewsBase
);
